import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { 
  X, ChevronRight, ChevronLeft, Check, HelpCircle,
  LayoutDashboard, Calendar, FileText, Users, Heart,
  DollarSign, Package, GraduationCap, BarChart3, Clock,
  Mic, Shield, Bell, Settings
} from 'lucide-react';

// Tutorial content for each role
const TUTORIAL_CONTENT = {
  super_admin: [
    {
      title: "Welcome to Integrity Home Care",
      description: "As a Super Administrator, you have full access to manage all tenants, users, and system settings. Let's walk through the key features.",
      icon: LayoutDashboard,
      highlight: null,
      position: 'center'
    },
    {
      title: "Dashboard Overview",
      description: "Your dashboard shows key metrics: documents, employees, trainings, active clients, and pending applicants. Click any card to navigate directly to that section.",
      icon: LayoutDashboard,
      highlight: '[data-testid="tab-overview"]',
      position: 'bottom'
    },
    {
      title: "Shift Management",
      description: "View and manage employee shifts, review completed shifts, and handle callouts. The calendar view shows all scheduled shifts across your organization.",
      icon: Calendar,
      highlight: '[data-testid="tab-shifts"]',
      position: 'bottom'
    },
    {
      title: "Policy Hub",
      description: "Access and manage company policies and procedures. Upload PDFs, organize into folders, and ensure staff can access the latest documents.",
      icon: FileText,
      highlight: '[data-testid="tab-policies"]',
      position: 'bottom'
    },
    {
      title: "User Management",
      description: "Add employees, review applicants, track compliance, and manage LMS trainings. Subtabs help you organize: Employees, Applicants, Compliance, LMS/Training, and Manage Trainings.",
      icon: Users,
      highlight: '[data-testid="tab-users"]',
      position: 'bottom'
    },
    {
      title: "Client Management",
      description: "Manage patient profiles, assign caregivers, and access Electronic Medical Records (EMR). Click any client to view details and open their full EMR.",
      icon: Heart,
      highlight: '[data-testid="tab-clients"]',
      position: 'bottom'
    },
    {
      title: "Billing & Claims",
      description: "Generate medical claims, manage billing rates, and track payments. Automated claim generation with CMS-1500 compliance.",
      icon: DollarSign,
      highlight: '[data-testid="tab-billing"]',
      position: 'bottom'
    },
    {
      title: "Supply Management",
      description: "Track inventory, scan supplies via barcode, and set up auto-reorder thresholds. Keep medical supplies organized and stocked.",
      icon: Package,
      highlight: '[data-testid="tab-supplies"]',
      position: 'bottom'
    },
    {
      title: "Reports & Analytics",
      description: "View comprehensive reports on shifts, compliance, billing, and more. Export data for analysis and regulatory requirements.",
      icon: BarChart3,
      highlight: '[data-testid="tab-reports"]',
      position: 'bottom'
    },
    {
      title: "Tenant Switching",
      description: "As Super Admin, use the tenant selector in the header to switch between organizations you manage. 'Exit' returns you to super admin view.",
      icon: Settings,
      highlight: null,
      position: 'top-right'
    },
    {
      title: "You're All Set!",
      description: "You can revisit this tutorial anytime by clicking the help icon (?) in the navigation bar. Happy managing!",
      icon: Check,
      highlight: null,
      position: 'center'
    }
  ],
  admin: [
    {
      title: "Welcome to Integrity Home Care",
      description: "As an Administrator, you can manage employees, clients, shifts, and access key operational features. Let's explore your dashboard.",
      icon: LayoutDashboard,
      highlight: null,
      position: 'center'
    },
    {
      title: "Dashboard Overview",
      description: "Quick stats show documents, employees, trainings, clients, and pending applicants. Click any card to jump directly to that section.",
      icon: LayoutDashboard,
      highlight: '[data-testid="tab-overview"]',
      position: 'bottom'
    },
    {
      title: "Shift Management",
      description: "Schedule and manage employee shifts. Review completed shifts, approve timesheets, and handle callout requests from caregivers.",
      icon: Calendar,
      highlight: '[data-testid="tab-shifts"]',
      position: 'bottom'
    },
    {
      title: "User Management",
      description: "Manage your team: add employees, review job applicants, track compliance expiration dates, and assign LMS trainings.",
      icon: Users,
      highlight: '[data-testid="tab-users"]',
      position: 'bottom'
    },
    {
      title: "Client Management",
      description: "View and manage patient profiles. Assign caregivers to clients and access their Electronic Medical Records (EMR) with one click.",
      icon: Heart,
      highlight: '[data-testid="tab-clients"]',
      position: 'bottom'
    },
    {
      title: "Billing & Claims",
      description: "Generate CMS-1500 claims, manage payer integrations, and track payment status. Streamline your medical billing workflow.",
      icon: DollarSign,
      highlight: '[data-testid="tab-billing"]',
      position: 'bottom'
    },
    {
      title: "Need Help?",
      description: "Click the help icon (?) anytime to revisit this tutorial. You can also use the Charlie chatbot in the bottom-right corner for quick questions.",
      icon: HelpCircle,
      highlight: null,
      position: 'center'
    }
  ],
  caregiver: [
    {
      title: "Welcome to Your Dashboard",
      description: "This is your personal hub for managing shifts, accessing client information, completing trainings, and documenting care.",
      icon: LayoutDashboard,
      highlight: null,
      position: 'center'
    },
    {
      title: "My Shifts",
      description: "View your upcoming and past shifts. Click 'Start Shift' when you arrive at a client's location to begin documenting your visit.",
      icon: Calendar,
      highlight: null,
      position: 'center'
    },
    {
      title: "Shift Toolkit",
      description: "During an active shift, access the toolkit to record vitals, complete incident reports with voice-to-text, and document care activities.",
      icon: Clock,
      highlight: null,
      position: 'center'
    },
    {
      title: "Voice Charting",
      description: "Use the microphone to dictate clinical notes. AI will transcribe and format your notes professionally - just speak naturally!",
      icon: Mic,
      highlight: null,
      position: 'center'
    },
    {
      title: "LMS Training",
      description: "Complete required trainings to stay compliant. View your assigned courses, take quizzes, and download certificates upon completion.",
      icon: GraduationCap,
      highlight: null,
      position: 'center'
    },
    {
      title: "Coverage & Callouts",
      description: "Need to call out? Submit a callout request. You can also pick up available shifts from coworkers who need coverage.",
      icon: Bell,
      highlight: null,
      position: 'center'
    },
    {
      title: "Compliance Tracker",
      description: "Keep track of your certifications, background checks, and TB tests. You'll see alerts when documents are expiring.",
      icon: Shield,
      highlight: null,
      position: 'center'
    },
    {
      title: "You're Ready!",
      description: "Click the help icon (?) in the header anytime to revisit this guide. Need more help? Chat with Charlie in the bottom-right corner!",
      icon: Check,
      highlight: null,
      position: 'center'
    }
  ]
};

// Get tutorial for role (default to caregiver for other roles like DSP, LPN, RN)
const getTutorialForRole = (role) => {
  if (role === 'super_admin') return TUTORIAL_CONTENT.super_admin;
  if (role === 'admin') return TUTORIAL_CONTENT.admin;
  return TUTORIAL_CONTENT.caregiver;
};

export default function TutorialOverlay({ onComplete, forceShow = false }) {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [showTutorial, setShowTutorial] = useState(false);
  const [highlightRect, setHighlightRect] = useState(null);
  const [loading, setLoading] = useState(true);

  const tutorialContent = user ? getTutorialForRole(user.role) : TUTORIAL_CONTENT.caregiver;
  const currentSlide = tutorialContent[currentStep];

  // Check if tutorial should be shown
  useEffect(() => {
    const checkTutorialStatus = async () => {
      if (forceShow) {
        setShowTutorial(true);
        setLoading(false);
        return;
      }

      // Check localStorage first (fast)
      const localStatus = localStorage.getItem(`tutorial_completed_${user?.id}`);
      if (localStatus === 'true') {
        setShowTutorial(false);
        setLoading(false);
        return;
      }

      // Then check API
      try {
        const response = await api.get('/user/preferences/tutorial');
        if (response.data.tutorial_completed) {
          localStorage.setItem(`tutorial_completed_${user?.id}`, 'true');
          setShowTutorial(false);
        } else {
          setShowTutorial(true);
        }
      } catch (error) {
        // If API fails, show tutorial if not in localStorage
        setShowTutorial(true);
      }
      setLoading(false);
    };

    if (user) {
      checkTutorialStatus();
    }
  }, [user, forceShow]);

  // Update highlight position when step changes
  useEffect(() => {
    if (currentSlide?.highlight) {
      const element = document.querySelector(currentSlide.highlight);
      if (element) {
        const rect = element.getBoundingClientRect();
        setHighlightRect({
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16
        });
      } else {
        setHighlightRect(null);
      }
    } else {
      setHighlightRect(null);
    }
  }, [currentStep, currentSlide]);

  const handleNext = () => {
    if (currentStep < tutorialContent.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = async () => {
    // Hide overlay immediately for better UX
    setShowTutorial(false);
    if (onComplete) onComplete();
    
    // Save to localStorage immediately
    localStorage.setItem(`tutorial_completed_${user?.id}`, 'true');
    
    // Save to API in background (fire-and-forget)
    try {
      await api.post('/user/preferences/tutorial/complete');
    } catch (error) {
      console.log('Tutorial completion saved locally');
    }
  };

  if (loading || !showTutorial) return null;

  const IconComponent = currentSlide.icon;

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="tutorial-overlay">
      {/* Dark overlay with cutout for highlighted element */}
      <div className="absolute inset-0 bg-black/70 transition-all duration-300">
        {highlightRect && (
          <div 
            className="absolute bg-transparent rounded-lg ring-4 ring-purple-400 ring-offset-2 shadow-lg"
            style={{
              top: highlightRect.top,
              left: highlightRect.left,
              width: highlightRect.width,
              height: highlightRect.height,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)'
            }}
          />
        )}
      </div>

      {/* Tutorial Card */}
      <div className={`absolute z-10 transition-all duration-300 ${
        currentSlide.position === 'center' ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' :
        currentSlide.position === 'top-right' ? 'top-24 right-8' :
        currentSlide.position === 'bottom' && highlightRect ? 
          `top-[${highlightRect.top + highlightRect.height + 20}px] left-1/2 -translate-x-1/2` :
        'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
      }`}
      style={currentSlide.position === 'bottom' && highlightRect ? {
        top: highlightRect.top + highlightRect.height + 20,
        left: '50%',
        transform: 'translateX(-50%)'
      } : {}}
      >
        <Card className="w-[420px] max-w-[90vw] bg-white shadow-2xl border-0 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-5 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <IconComponent className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">{currentSlide.title}</h3>
                  <p className="text-purple-200 text-sm">
                    Step {currentStep + 1} of {tutorialContent.length}
                  </p>
                </div>
              </div>
              <button
                onClick={handleSkip}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                data-testid="tutorial-skip-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-5">
            <p className="text-gray-700 leading-relaxed">
              {currentSlide.description}
            </p>
          </div>

          {/* Progress bar */}
          <div className="px-5 pb-2">
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-purple-600 transition-all duration-300 rounded-full"
                style={{ width: `${((currentStep + 1) / tutorialContent.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="p-5 pt-3 flex items-center justify-between border-t border-gray-100">
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="text-gray-500 hover:text-gray-700"
              data-testid="tutorial-skip-text-btn"
            >
              Skip Tutorial
            </Button>
            <div className="flex gap-2">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  className="border-purple-200 text-purple-700 hover:bg-purple-50"
                  data-testid="tutorial-prev-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              )}
              <Button
                onClick={handleNext}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="tutorial-next-btn"
              >
                {currentStep === tutorialContent.length - 1 ? (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Get Started
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

// Help Icon component to recall tutorial
export function TutorialHelpIcon() {
  const { user } = useAuth();
  const [showTutorial, setShowTutorial] = useState(false);
  const [hasShownInitial, setHasShownInitial] = useState(false);

  // Check on mount if we should auto-show tutorial (first login)
  useEffect(() => {
    const checkInitialTutorial = async () => {
      if (!user || hasShownInitial) return;
      
      const localStatus = localStorage.getItem(`tutorial_completed_${user?.id}`);
      if (localStatus === 'true') {
        setHasShownInitial(true);
        return;
      }

      try {
        const response = await api.get('/user/preferences/tutorial');
        if (!response.data.tutorial_completed) {
          setShowTutorial(true);
        } else {
          localStorage.setItem(`tutorial_completed_${user?.id}`, 'true');
        }
      } catch (error) {
        // If API fails, show tutorial if not completed locally
        setShowTutorial(true);
      }
      setHasShownInitial(true);
    };

    checkInitialTutorial();
  }, [user, hasShownInitial]);

  const handleClick = async () => {
    // Reset tutorial status and show again
    localStorage.removeItem(`tutorial_completed_${user?.id}`);
    try {
      await api.post('/user/preferences/tutorial/reset');
    } catch (error) {
      console.log('Tutorial reset locally');
    }
    setShowTutorial(true);
  };

  const handleComplete = () => {
    setShowTutorial(false);
  };

  return (
    <>
      <button
        onClick={handleClick}
        className="p-2 text-gray-500 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-all duration-200"
        title="Show Tutorial"
        data-testid="tutorial-help-icon"
      >
        <HelpCircle className="w-5 h-5" />
      </button>
      {showTutorial && (
        <TutorialOverlay 
          forceShow={true} 
          onComplete={handleComplete} 
        />
      )}
    </>
  );
}
